import { subscriptionPlanLabel, formatCreditCount } from "@shared/subscription-presentation";
import React, { useState, useEffect, useRef } from 'react';
import { endImpersonation, isImpersonating } from "@/lib/impersonation";
import { useLocation } from 'wouter';
import {
  X,
  User,
  Settings,
  BarChart2,
  Link2,
  Database,
  HelpCircle,
  LogOut,
  Zap,
} from 'lucide-react';
import ConnectionsContent from './connections-content';
import { CrmConnectionSettings } from './crm-connection-form';
import { SettingsUsageTab } from './settings-usage-tab';
import { UpgradeModal } from './upgrade-modal';
import { ViewAllUsageModal } from './view-all-usage-modal';
import { useToast } from '@/hooks/use-toast';
import { useAuthUser } from '../App';
import { useUsageBalance } from '@/hooks/use-usage';

export type SettingsTab = 'account' | 'settings' | 'usage' | 'connections' | 'data' | 'crm';

const NAV_ITEMS: { id: SettingsTab; label: string; icon: React.ElementType }[] = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'usage', label: 'Usage', icon: BarChart2 },
  { id: 'connections', label: 'Connections', icon: Link2 },
  { id: 'data', label: 'Data', icon: Database },
  { id: 'crm', label: 'CRM', icon: Database },
];

function ComingSoonTab({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 12,
      }}
    >
      <Icon style={{ width: 36, height: 36, color: '#C8D3E2' }} />
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: '#6B6B6B', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: '#5F6D82' }}>{subtitle}</div>
      </div>
    </div>
  );
}

interface SettingsModalProps {
  onClose: () => void;
  initialTab?: SettingsTab;
}

export function SettingsModal({ onClose, initialTab }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab || 'account');
  const [isVisible, setIsVisible] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const backdropRef = useRef<HTMLDivElement>(null);
  const user = useAuthUser();
  const usageBalance = useUsageBalance();

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'DH';

  const userName = user?.name || 'Dan Howes';
  const userEmail = user?.email || '';

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 150);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) handleClose();
  };

  const handleSignOut = async () => {
    // v7.54 — inside an admin "Login as" window, sign-out ends that session only.
    if (isImpersonating()) { await endImpersonation(); return; }
    // credentials: 'include' is required — without the session cookie the
    // server can't destroy the session and the user stays logged in.
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    onClose();
    // Full reload so App.tsx's auth context re-fetches and lands at /login
    // cleanly. wouter's navigate alone leaves cached user state and the
    // auth-bounce useEffect would push us back into the app.
    window.location.href = '/login';
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  // Cancellation lives in the Stripe Customer Portal — the same place "Manage
  // plan" opens — so there's one consistent surface and Stripe owns the
  // cancel-at-period-end flow (and its confirmation).
  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) throw new Error(data?.message || 'Couldn’t open billing portal');
      window.location.href = data.url; // → Stripe Customer Portal (cancel there)
    } catch (err: any) {
      toast({ title: 'Couldn’t open billing portal', description: err.message ?? 'Please try again.' });
      setCancellingSubscription(false);
    }
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      data-testid="settings-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 150ms ease',
      }}
    >
      <div
        data-testid="settings-modal"
        style={{
          width: '80vw',
          maxWidth: 800,
          height: '80vh',
          maxHeight: 600,
          background: '#FFFFFF',
          borderRadius: 16,
          border: '1px solid #DCE3ED',
          boxShadow: '0 8px 32px rgba(30,45,65,0.13), 0 2px 8px rgba(30,45,65,0.07)',
          display: 'flex',
          overflow: 'hidden',
          transform: isVisible ? 'scale(1)' : 'scale(0.96)',
          transition: 'transform 150ms ease, opacity 150ms ease',
          opacity: isVisible ? 1 : 0,
        }}
      >
        {/* Left nav sidebar */}
        <div className="workspace-settings-nav"
          style={{
            width: 200,
            flexShrink: 0,
            background: '#F7F9FC',
            borderRight: '1px solid #DCE3ED',
            display: 'flex',
            flexDirection: 'column',
            padding: '20px 10px',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '0 8px', marginBottom: 8 }}>
            Settings
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  data-testid={`settings-nav-${item.id}`}
                  onClick={() => setActiveTab(item.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 8px',
                    borderRadius: 8,
                    fontSize: 13.5,
                    fontWeight: isActive ? 500 : 400,
                    color: isActive ? '#1A1A1A' : '#526074',
                    background: isActive ? '#EBEBEB' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    position: 'relative',
                    transition: 'background 120ms',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  {isActive && (
                    <span
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3,
                        height: 16,
                        borderRadius: 2,
                        background: '#7B1F40',
                      }}
                    />
                  )}
                  <Icon style={{ width: 15, height: 15, color: isActive ? '#7B1F40' : '#5F6D82', flexShrink: 0 }} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Get help link */}
          <button
            data-testid="settings-nav-help"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 8px',
              borderRadius: 8,
              fontSize: 13,
              color: '#5F6D82',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <HelpCircle style={{ width: 15, height: 15, flexShrink: 0 }} />
            <span>Get help</span>
          </button>
        </div>

        {/* Right content area */}
        <div className="workspace-settings-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 24px 14px',
              borderBottom: '1px solid #EAF0F7',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>
              {NAV_ITEMS.find((n) => n.id === activeTab)?.label || 'Settings'}
            </span>
            <button
              data-testid="settings-modal-close"
              aria-label="Close settings"
              onClick={handleClose}
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#5F6D82',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {activeTab === 'account' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 420 }}>
                {/* User card */}
                <div
                  data-testid="settings-user-card"
                  style={{
                    border: '1px solid #DCE3ED',
                    borderRadius: 12,
                    padding: '16px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    background: '#F7F9FC',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: '#DCE3ED',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#334155',
                      fontSize: 16,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                    data-testid="settings-user-avatar"
                  >
                    {initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', marginBottom: 2 }} data-testid="settings-user-name">
                      {userName}
                    </div>
                    <div style={{ fontSize: 12.5, color: '#5F6D82', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid="settings-user-email">
                      {userEmail}
                    </div>
                  </div>
                </div>

                {/* Plan card */}
                <div
                  data-testid="settings-plan-card"
                  style={{
                    border: '1px solid #DCE3ED',
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: '#F7F9FC',
                  }}
                >
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid #EAF0F7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Zap style={{ width: 14, height: 14, color: '#7B1F40' }} />
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1A1A1A' }} data-testid="settings-plan-name">
                        {usageBalance.isLoading
                          ? 'Loading…'
                          : usageBalance.data?.plan?.name
                            ? subscriptionPlanLabel(usageBalance.data.plan)
                            : 'No plan'}
                      </span>
                    </div>
                    <button
                      data-testid="settings-upgrade-btn"
                      onClick={handleUpgrade}
                      style={{
                        padding: '5px 14px',
                        borderRadius: 7,
                        background: 'transparent',
                        color: '#7B1F40',
                        fontSize: 12.5,
                        fontWeight: 500,
                        border: '1px solid #7B1F40',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        transition: 'background 120ms',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0F0FE'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                    >
                      {usageBalance.data?.plan ? 'Upgrade' : 'Choose plan'}
                    </button>
                  </div>
                  <div style={{ padding: '10px 0' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 18px',
                      }}
                    >
                      <span style={{ fontSize: 13, color: '#526074' }}>Credits remaining</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }} data-testid="settings-credits">
                        {usageBalance.isLoading
                          ? '—'
                          : usageBalance.data
                            ? `${formatCreditCount(usageBalance.data.balance)}${
                                usageBalance.data.plan
                                  ? ` / ${formatCreditCount(usageBalance.data.plan.creditsPerMonth)}`
                                  : ''
                              }`
                            : '—'}
                      </span>
                    </div>
                    {!usageBalance.data?.plan?.isPromo && usageBalance.data?.plan?.renewsAt && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 18px',
                          borderTop: '1px solid #EAF0F7',
                        }}
                      >
                        <span style={{ fontSize: 13, color: '#526074' }}>
                          {usageBalance.data?.plan?.status === 'trialing' ? 'Trial ends' : 'Renews'}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }} data-testid="settings-renews">
                          {new Date(usageBalance.data.plan.renewsAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom action row — Sign out left, Cancel Subscription right */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 2 }}>
                  <button
                    data-testid="settings-signout-link"
                    onClick={handleSignOut}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: '#5F6D82',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 0',
                      transition: 'color 120ms',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#526074'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#5F6D82'; }}
                  >
                    <LogOut style={{ width: 13, height: 13 }} />
                    Sign out
                  </button>
                  {usageBalance.data?.plan && (
                    <button
                      data-testid="settings-cancel-subscription"
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 13,
                        color: '#A8443D',
                        background: 'transparent',
                        border: 'none',
                        cursor: cancellingSubscription ? 'wait' : 'pointer',
                        padding: '4px 0',
                        opacity: cancellingSubscription ? 0.6 : 1,
                        transition: 'color 120ms',
                      }}
                      onMouseEnter={(e) => { if (!cancellingSubscription) (e.currentTarget as HTMLButtonElement).style.color = '#8A2F28'; }}
                      onMouseLeave={(e) => { if (!cancellingSubscription) (e.currentTarget as HTMLButtonElement).style.color = '#A8443D'; }}
                    >
                      {cancellingSubscription ? 'Opening…' : 'Cancel subscription'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <ComingSoonTab
                icon={Settings}
                title="Settings"
                subtitle="Preferences and configuration coming soon"
              />
            )}

            {activeTab === 'usage' && (
              <SettingsUsageTab
                onUpgrade={() => setShowUpgradeModal(true)}
                onViewAll={() => setShowViewAllModal(true)}
              />
            )}

            {activeTab === 'connections' && (
              <ConnectionsContent />
            )}

            {activeTab === 'data' && (
              <ComingSoonTab
                icon={Database}
                title="Data"
                subtitle="Data management and export coming soon"
              />
            )}

            {activeTab === 'crm' && (
              <CrmConnectionSettings />
            )}
          </div>
        </div>
      </div>
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      {showViewAllModal && <ViewAllUsageModal onClose={() => setShowViewAllModal(false)} />}
    </div>
  );
}
